import React from 'react';
import { useTranslation } from 'react-i18next';

import { ISelectOption } from '@private/components';

import {
  FILTER_OPERATIONS,
  IFilterValue,
  ISelectFilterProps,
} from 'components/Grids/DataGrid/Filters/utils/types';
import { INullable } from 'utils/types';

import { StyledInput } from '../styled';

const isArrayString = (v: unknown): v is string[] => {
  if (!Array.isArray(v)) {
    return false;
  }

  if (!v.length) {
    return true;
  }

  return v.every((v) => typeof v === 'string');
};

const isSelectOption = (v: unknown): v is ISelectOption => {
  if (typeof v === 'object' && v !== null) {
    return (
      Object.prototype.hasOwnProperty.call(v, 'value') &&
      Object.prototype.hasOwnProperty.call(v, 'label')
    );
  }
  return false;
};

const isSelectOptions = (v: unknown): v is ISelectOption[] => {
  if (!Array.isArray(v)) {
    return false;
  }

  if (!v.length) {
    return true;
  }

  return v.every(isSelectOption);
};

export const getValuesList = (value: IFilterValue): string[] => {
  if (isArrayString(value)) {
    return value;
  }

  if (isSelectOptions(value)) {
    return [value[0].value as string];
  }

  if (isSelectOption(value)) {
    return [value.value as string];
  }

  return [value as string];
};

interface IFilterInput {
  columnKey: string;
  commonInputProps: Record<string, unknown>;
  focusedFieldName: INullable<{ columnKey: string; index?: number }>;
  index?: number;
  isAutoFocused: boolean;
  multiSelect?: boolean;
  onSelectFilter: (data: ISelectFilterProps) => void;
  operation: INullable<FILTER_OPERATIONS>;
  value?: IFilterValue;
}

const FilterInput = ({
  columnKey,
  commonInputProps,
  focusedFieldName,
  index = 0,
  isAutoFocused,
  multiSelect,
  onSelectFilter,
  operation,
  value,
}: IFilterInput) => {
  const { t } = useTranslation();

  const ref = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isAutoFocused) {
      setTimeout(() => ref.current?.scrollIntoView({ block: 'center' }), 300);
    }
  }, [isAutoFocused]);

  const onInputFilterChange =
    (index?: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      let newValue: string | string[] = inputValue;
      if (multiSelect) {
        newValue = Array.isArray(value)
          ? (value as string[]).map((item, itemIndex) =>
              index === itemIndex ? inputValue : item,
            )
          : [inputValue];
      }
      onSelectFilter({
        columnKey,
        operation,
        value: newValue,
      });
    };

  const valuesList = getValuesList(value);

  return (
    <StyledInput
      autoFocus={
        multiSelect
          ? isAutoFocused && (focusedFieldName?.index || 0) === index
          : isAutoFocused
      }
      inputRef={ref}
      isAutoFocused={
        multiSelect
          ? isAutoFocused && (focusedFieldName?.index || 0) === index
          : isAutoFocused
      }
      onChange={onInputFilterChange(index)}
      placeholder={t('common.filters.inputSearchPlaceholder')}
      testIds={{ input: `${columnKey}Input${index !== undefined ? `-${index}` : ''}` }}
      value={
        (multiSelect && Array.isArray(value) ? valuesList?.[index] : valuesList[0]) || ''
      }
      {...commonInputProps}
    />
  );
};

export default FilterInput;
